import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Eye, Search, Table2, Terminal } from "lucide-react";
import type { Schema } from "@/lib/api";
import { seedSelectSQL } from "@/lib/api";
import { Spinner } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export type SchemaExplorerProps = {
  projectId: string;
  schema: Schema | null;
  loading?: boolean;
  /** Highlight selected relation name */
  selected?: { kind: "table" | "view"; name: string } | null;
  /** When set, clicking an object also invokes this (e.g. seed SQL). */
  onOpenSQL?: (sql: string, relation: { kind: "table" | "view"; name: string }) => void;
  className?: string;
};

export function SchemaExplorer({
  projectId,
  schema,
  loading,
  selected,
  onOpenSQL,
  className,
}: SchemaExplorerProps) {
  const [q, setQ] = useState("");
  const filter = q.trim().toLowerCase();

  const tables = useMemo(() => {
    const list = schema?.tables ?? [];
    if (!filter) return list;
    return list.filter((t) => t.name.toLowerCase().includes(filter));
  }, [schema, filter]);

  const views = useMemo(() => {
    const list = schema?.views ?? [];
    if (!filter) return list;
    return list.filter((v) => v.name.toLowerCase().includes(filter));
  }, [schema, filter]);

  return (
    <aside
      className={cn(
        "flex w-full flex-col rounded-xl border border-border-standard bg-surface-container-lowest shadow-none lg:w-64 lg:shrink-0",
        className,
      )}
      data-testid="schema-explorer"
    >
      <div className="border-b border-border-standard px-3 py-2.5">
        <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-text-disabled">
          Schema
        </div>
        <div
          className="mono-chip max-w-full truncate !py-0.5"
          title={schema?.schema_name ?? "…"}
          data-testid="schema-name-badge"
        >
          {schema?.schema_name ?? (loading ? "loading…" : "—")}
        </div>
        <p className="mt-1.5 text-[11px] leading-snug text-text-muted">
          Project schema only — no multi-schema switcher.
        </p>
      </div>

      <div className="border-b border-border-standard px-2 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-disabled" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter tables & views"
            className="h-8 pl-7 text-xs"
            aria-label="Filter schema objects"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {loading && !schema ? (
          <div className="flex items-center gap-2 px-1 py-3">
            <Spinner
              label="Loading schema…"
              size="sm"
              center={false}
              className="text-xs"
            />
          </div>
        ) : null}

        <Section
          title="Tables"
          count={tables.length}
          empty={
            !loading && tables.length === 0
              ? filter
                ? "No matching tables"
                : "No tables yet"
              : null
          }
        >
          {tables.map((t) => (
            <ObjectRow
              key={`t:${t.name}`}
              projectId={projectId}
              kind="table"
              name={t.name}
              columns={t.columns.length}
              selected={selected?.kind === "table" && selected.name === t.name}
              href={`/dashboard/projects/${projectId}/tables/${encodeURIComponent(t.name)}`}
              sqlHref={`/dashboard/projects/${projectId}/sql?relation=${encodeURIComponent(t.name)}&kind=table`}
              onOpenSQL={
                onOpenSQL
                  ? () =>
                      onOpenSQL(seedSelectSQL(t.name), {
                        kind: "table",
                        name: t.name,
                      })
                  : undefined
              }
            />
          ))}
        </Section>

        <Section
          title="Views"
          count={views.length}
          empty={
            !loading && views.length === 0
              ? filter
                ? "No matching views"
                : "No views yet"
              : null
          }
        >
          {views.map((v) => (
            <ObjectRow
              key={`v:${v.name}`}
              projectId={projectId}
              kind="view"
              name={v.name}
              columns={v.columns.length}
              selected={selected?.kind === "view" && selected.name === v.name}
              href={`/dashboard/projects/${projectId}/views/${encodeURIComponent(v.name)}`}
              sqlHref={`/dashboard/projects/${projectId}/sql?relation=${encodeURIComponent(v.name)}&kind=view`}
              onOpenSQL={
                onOpenSQL
                  ? () =>
                      onOpenSQL(seedSelectSQL(v.name), {
                        kind: "view",
                        name: v.name,
                      })
                  : undefined
              }
            />
          ))}
        </Section>
      </div>
    </aside>
  );
}

function Section({
  title,
  count,
  empty,
  children,
}: {
  title: string;
  count: number;
  empty: string | null;
  children: ReactNode;
}) {
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-disabled">
          {title}
        </span>
        <Badge variant="secondary" className="tabular-nums !text-[10px]">
          {count}
        </Badge>
      </div>
      {empty ? (
        <p className="px-1 py-1 text-[11px] text-text-muted">{empty}</p>
      ) : (
        <ul className="space-y-0.5">{children}</ul>
      )}
    </div>
  );
}

function ObjectRow({
  kind,
  name,
  columns,
  selected,
  href,
  sqlHref,
  onOpenSQL,
}: {
  projectId: string;
  kind: "table" | "view";
  name: string;
  columns: number;
  selected: boolean;
  href: string;
  sqlHref: string;
  onOpenSQL?: () => void;
}) {
  const Icon = kind === "table" ? Table2 : Eye;
  return (
    <li
      className={cn(
        "group rounded-md border border-transparent",
        selected && "border-secondary/30 bg-primary/5",
      )}
    >
      <div className="flex items-center gap-0.5">
        <Link
          to={href}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left text-xs hover:bg-surface-container-low",
            selected && "text-secondary",
          )}
          title={name}
        >
          <Icon className="h-3.5 w-3.5 shrink-0 text-text-disabled" />
          <span className="truncate font-medium text-text-primary">{name}</span>
          <span className="ml-auto shrink-0 tabular-nums text-[10px] text-text-disabled">
            {columns}
          </span>
        </Link>
        {onOpenSQL ? (
          <button
            type="button"
            className="mr-0.5 rounded p-1 text-text-disabled opacity-0 hover:bg-surface-container-low hover:text-secondary group-hover:opacity-100"
            title="Open in SQL"
            aria-label={`Open ${name} in SQL`}
            onClick={onOpenSQL}
          >
            <Terminal className="h-3.5 w-3.5" />
          </button>
        ) : (
          <Link
            to={sqlHref}
            className="mr-0.5 rounded p-1 text-text-disabled opacity-0 hover:bg-surface-container-low hover:text-secondary group-hover:opacity-100"
            title="Open in SQL"
            aria-label={`Open ${name} in SQL`}
          >
            <Terminal className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    </li>
  );
}
